import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  BarChart3,
  TrendingUp,
  TrendingDown,
  Users,
  Eye,
  Heart,
  MessageCircle,
  Share2,
  MousePointer,
  Calendar,
  Download,
  RefreshCw,
  ChevronDown,
  Check,
  AlertCircle,
  Loader2,
} from 'lucide-react';
import { api, type SMCCTopContent } from '../../../services/api';
import { getPeriodDateRange, formatNumber, formatDateForAPI } from '../../../utils/smcc';
import { PLATFORM_CONFIG, type AnalyticsPeriod, type SMCCPlatformId } from '../../../constants/smcc';

type Period = AnalyticsPeriod;

function StatCard({
  title,
  value,
  change,
  changeType = 'neutral',
  icon: Icon,
  subtitle,
}: {
  title: string;
  value: string | number;
  change?: number;
  changeType?: 'up' | 'down' | 'neutral';
  icon: typeof BarChart3;
  subtitle?: string;
}) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm text-gray-500">{title}</span>
        <Icon className="w-4 h-4 text-gray-400" />
      </div>
      <p className="text-2xl font-bold text-gray-900">{typeof value === 'number' ? formatNumber(value) : value}</p>
      {(change !== undefined || subtitle) && (
        <div className="flex items-center gap-1 mt-1">
          {change !== undefined && (
            <>
              {changeType === 'up' && <TrendingUp className="w-3 h-3 text-green-500" />}
              {changeType === 'down' && <TrendingDown className="w-3 h-3 text-red-500" />}
              <span
                className={`text-xs ${
                  changeType === 'up'
                    ? 'text-green-600'
                    : changeType === 'down'
                    ? 'text-red-600'
                    : 'text-gray-500'
                }`}
              >
                {change > 0 ? '+' : ''}{change}%
              </span>
            </>
          )}
          {subtitle && <span className="text-xs text-gray-500">{subtitle}</span>}
        </div>
      )}
    </div>
  );
}

function PlatformComparisonCard({
  platforms,
}: {
  platforms: Array<{
    platform: string;
    followers: number;
    engagement: number;
    engagement_rate: number;
    impressions: number;
  }>;
}) {
  const maxEngagement = Math.max(...platforms.map((p) => p.engagement), 1);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
      <h3 className="font-semibold text-gray-900 mb-4">Platform Performance</h3>
      <div className="space-y-3">
        {platforms.map((p) => {
          const config = PLATFORM_CONFIG[p.platform as SMCCPlatformId];
          const widthPercent = (p.engagement / maxEngagement) * 100;

          return (
            <div key={p.platform} className="flex items-center gap-3">
              <div
                className="w-3 h-3 rounded-full flex-shrink-0"
                style={{ backgroundColor: config?.color || '#gray' }}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-gray-700">
                    {config?.name || p.platform}
                  </span>
                  <span className="text-xs text-gray-500">
                    {formatNumber(p.engagement)} engagements
                  </span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{
                      width: `${widthPercent}%`,
                      backgroundColor: config?.color || '#gray',
                    }}
                  />
                </div>
                <div className="flex items-center justify-between mt-1 text-xs text-gray-400">
                  <span>{formatNumber(p.followers)} followers</span>
                  <span>{p.engagement_rate}% engagement rate</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TopContentCard({
  content,
}: {
  content: SMCCTopContent[];
}) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
      <h3 className="font-semibold text-gray-900 mb-4">Top Performing Content</h3>
      {content.length === 0 ? (
        <p className="text-sm text-gray-500 text-center py-4">No published content yet</p>
      ) : (
        <div className="space-y-3">
          {content.slice(0, 5).map((item, idx) => (
            <div key={item.id} className="flex items-start gap-3">
              <span className="text-lg font-bold text-gray-300 w-6">{idx + 1}</span>
              <div className="w-12 h-12 rounded-lg bg-gray-100 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {item.title || 'Untitled'}
                </p>
                <div className="flex items-center gap-3 text-xs text-gray-500 mt-1">
                  <span className="flex items-center gap-1">
                    <Heart className="w-3 h-3" /> {formatNumber(item.likes)}
                  </span>
                  <span className="flex items-center gap-1">
                    <MessageCircle className="w-3 h-3" /> {formatNumber(item.comments)}
                  </span>
                  <span className="flex items-center gap-1">
                    <Share2 className="w-3 h-3" /> {formatNumber(item.shares)}
                  </span>
                </div>
              </div>
              <div className="text-right flex-shrink-0">
                <span className="text-sm font-semibold text-primary-600">
                  {item.engagement_rate}%
                </span>
                <p className="text-xs text-gray-400">engagement</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TimelineChart({
  data,
  metric,
}: {
  data: Array<{
    date: string;
    followers: number;
    impressions: number;
    reach: number;
    engagement: number;
    likes: number;
    comments: number;
    shares: number;
  }>;
  metric: string;
}) {
  if (data.length === 0) {
    return (
      <div className="h-64 flex items-center justify-center text-gray-500">
        No data available for this period
      </div>
    );
  }

  const metricKey = metric as keyof typeof data[0];
  const values = data.map((d) => d[metricKey] as number);
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const range = max - min || 1;

  return (
    <div className="h-64 flex items-end gap-1">
      {data.map((item, idx) => {
        const value = item[metricKey] as number;
        const height = ((value - min) / range) * 100;

        return (
          <div
            key={idx}
            className="flex-1 group relative"
          >
            <div
              className="bg-primary-500 hover:bg-primary-600 rounded-t transition-all cursor-pointer"
              style={{ height: `${Math.max(height, 2)}%` }}
            />
            {/* Tooltip */}
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-10">
              <div className="bg-gray-900 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                <div className="font-medium">{formatNumber(value)}</div>
                <div className="text-gray-400">{new Date(item.date).toLocaleDateString()}</div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function SMCCAnalytics() {
  const [period, setPeriod] = useState<Period>('7d');
  const [selectedMetric, setSelectedMetric] = useState('engagement');
  const [exportLoading, setExportLoading] = useState(false);
  const [exportMessage, setExportMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Fetch dashboard metrics
  const { data: dashboardData, isLoading: loadingDashboard, refetch } = useQuery({
    queryKey: ['smcc-analytics-dashboard', period],
    queryFn: () => api.getSMCCAnalyticsDashboard({ period }),
  });

  // Fetch timeline
  const { data: timelineData, isLoading: loadingTimeline } = useQuery({
    queryKey: ['smcc-analytics-timeline', period, selectedMetric],
    queryFn: () => {
      const now = new Date();
      let startDate: Date;

      switch (period) {
        case '24h':
          startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
          break;
        case '7d':
          startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;
        case '30d':
          startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          break;
        case '90d':
          startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
          break;
      }

      return api.getSMCCAnalyticsTimeline({
        start_date: startDate.toISOString().split('T')[0],
        end_date: now.toISOString().split('T')[0],
        metric: selectedMetric,
      });
    },
  });

  // Fetch platform comparison
  const { data: platformData } = useQuery({
    queryKey: ['smcc-analytics-platforms', period],
    queryFn: () => {
      const now = new Date();
      let startDate: Date;

      switch (period) {
        case '24h':
          startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
          break;
        case '7d':
          startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;
        case '30d':
          startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          break;
        case '90d':
          startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
          break;
      }

      return api.getSMCCAnalyticsPlatforms({
        start_date: startDate.toISOString().split('T')[0],
        end_date: now.toISOString().split('T')[0],
      });
    },
  });

  // Fetch top content
  const { data: topContentData } = useQuery({
    queryKey: ['smcc-analytics-top-content', period],
    queryFn: () => {
      const now = new Date();
      let startDate: Date;

      switch (period) {
        case '24h':
          startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
          break;
        case '7d':
          startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;
        case '30d':
          startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          break;
        case '90d':
          startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
          break;
      }

      return api.getSMCCAnalyticsTopContent({
        start_date: startDate.toISOString().split('T')[0],
        end_date: now.toISOString().split('T')[0],
        limit: 10,
      });
    },
  });

  const metrics = dashboardData?.metrics;
  const timeline = timelineData?.timeline || [];
  const platforms = platformData?.platforms || [];
  const topContent = topContentData?.content || [];

  const handleExport = async (format: 'json' | 'csv') => {
    setExportLoading(true);
    setExportMessage(null);

    try {
      const { startDate, endDate } = getPeriodDateRange(period);
      const start_date = formatDateForAPI(startDate);
      const end_date = formatDateForAPI(endDate);

      if (format === 'csv') {
        const response = await api.exportSMCCAnalytics({
          start_date,
          end_date,
          format: 'csv',
        });

        if (response instanceof Response) {
          const blob = await response.blob();
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `smcc-analytics-${period}.csv`;
          a.click();
          URL.revokeObjectURL(url);
          setExportMessage({ type: 'success', text: 'CSV export downloaded' });
        }
      } else if (format === 'json') {
        // Gather all analytics data for JSON export
        const exportData = {
          period,
          dateRange: { start_date, end_date },
          exportedAt: new Date().toISOString(),
          metrics: dashboardData?.metrics || {},
          platformBreakdown: platformData?.platforms || [],
          topContent: topContentData?.content || [],
          timeline: timelineData?.timeline || [],
        };

        const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `smcc-analytics-${period}.json`;
        a.click();
        URL.revokeObjectURL(url);
        setExportMessage({ type: 'success', text: 'JSON export downloaded' });
      }
    } catch (error) {
      console.error('Export failed:', error);
      setExportMessage({ type: 'error', text: 'Export failed. Please try again.' });
    } finally {
      setExportLoading(false);
      setTimeout(() => setExportMessage(null), 3000);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analytics</h1>
          <p className="text-gray-500 mt-1">Track your social media performance</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Period selector */}
          <div className="relative">
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value as Period)}
              className="appearance-none bg-white border border-gray-200 rounded-lg px-4 py-2 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="24h">Last 24 Hours</option>
              <option value="7d">Last 7 Days</option>
              <option value="30d">Last 30 Days</option>
              <option value="90d">Last 90 Days</option>
            </select>
            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          </div>

          {/* Export */}
          <div className="relative group">
            <button
              disabled={exportLoading}
              className="px-4 py-2 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center gap-2 disabled:opacity-50"
            >
              {exportLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              {exportLoading ? 'Exporting...' : 'Export'}
            </button>
            {!exportLoading && (
              <div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 hidden group-hover:block z-10 min-w-[150px]">
                <button
                  onClick={() => handleExport('csv')}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  Export as CSV
                </button>
                <button
                  onClick={() => handleExport('json')}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  Export as JSON
                </button>
              </div>
            )}
          </div>

          {/* Export Message */}
          {exportMessage && (
            <div
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm ${
                exportMessage.type === 'success'
                  ? 'bg-green-50 text-green-700'
                  : 'bg-red-50 text-red-700'
              }`}
              role="alert"
            >
              {exportMessage.type === 'success' ? (
                <Check className="w-4 h-4" />
              ) : (
                <AlertCircle className="w-4 h-4" />
              )}
              {exportMessage.text}
            </div>
          )}

          {/* Refresh */}
          <button
            onClick={() => refetch()}
            className="p-2 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            <RefreshCw className={`w-4 h-4 ${loadingDashboard ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <StatCard
          title="Total Followers"
          value={metrics?.total_followers || 0}
          change={metrics?.followers_change_percent}
          changeType={
            (metrics?.followers_change_percent || 0) > 0
              ? 'up'
              : (metrics?.followers_change_percent || 0) < 0
              ? 'down'
              : 'neutral'
          }
          icon={Users}
        />
        <StatCard
          title="Impressions"
          value={metrics?.total_impressions || 0}
          change={metrics?.impressions_change_percent}
          changeType={
            (metrics?.impressions_change_percent || 0) > 0
              ? 'up'
              : (metrics?.impressions_change_percent || 0) < 0
              ? 'down'
              : 'neutral'
          }
          icon={Eye}
        />
        <StatCard
          title="Reach"
          value={metrics?.total_reach || 0}
          change={metrics?.reach_change_percent}
          changeType={
            (metrics?.reach_change_percent || 0) > 0
              ? 'up'
              : (metrics?.reach_change_percent || 0) < 0
              ? 'down'
              : 'neutral'
          }
          icon={Users}
        />
        <StatCard
          title="Engagement"
          value={metrics?.total_engagement || 0}
          change={metrics?.engagement_change_percent}
          changeType={
            (metrics?.engagement_change_percent || 0) > 0
              ? 'up'
              : (metrics?.engagement_change_percent || 0) < 0
              ? 'down'
              : 'neutral'
          }
          icon={Heart}
        />
        <StatCard
          title="Engagement Rate"
          value={`${metrics?.avg_engagement_rate || 0}%`}
          icon={TrendingUp}
        />
        <StatCard
          title="Posts Published"
          value={metrics?.posts_published || 0}
          icon={Calendar}
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Timeline Chart */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900">Performance Over Time</h3>
            <select
              value={selectedMetric}
              onChange={(e) => setSelectedMetric(e.target.value)}
              className="text-sm border border-gray-200 rounded-lg px-3 py-1 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="engagement">Engagement</option>
              <option value="impressions">Impressions</option>
              <option value="reach">Reach</option>
              <option value="followers">Followers</option>
              <option value="likes">Likes</option>
              <option value="comments">Comments</option>
            </select>
          </div>
          {loadingTimeline ? (
            <div className="h-64 flex items-center justify-center">
              <RefreshCw className="w-6 h-6 text-gray-400 animate-spin" />
            </div>
          ) : (
            <TimelineChart data={timeline} metric={selectedMetric} />
          )}
        </div>

        {/* Platform Comparison */}
        <PlatformComparisonCard platforms={platforms} />
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <TopContentCard content={topContent} />

        {/* Quick Stats */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <h3 className="font-semibold text-gray-900 mb-4">Additional Metrics</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2 text-gray-500 mb-1">
                <Eye className="w-4 h-4" />
                <span className="text-sm">Profile Views</span>
              </div>
              <p className="text-xl font-bold text-gray-900">
                {formatNumber(metrics?.profile_views || 0)}
              </p>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2 text-gray-500 mb-1">
                <MousePointer className="w-4 h-4" />
                <span className="text-sm">Website Clicks</span>
              </div>
              <p className="text-xl font-bold text-gray-900">
                {formatNumber(metrics?.website_clicks || 0)}
              </p>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2 text-gray-500 mb-1">
                <Calendar className="w-4 h-4" />
                <span className="text-sm">Scheduled</span>
              </div>
              <p className="text-xl font-bold text-gray-900">
                {metrics?.scheduled_count || 0}
              </p>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2 text-gray-500 mb-1">
                <BarChart3 className="w-4 h-4" />
                <span className="text-sm">Pending Approval</span>
              </div>
              <p className="text-xl font-bold text-gray-900">
                {metrics?.pending_approval_count || 0}
              </p>
            </div>
          </div>

          {metrics?.top_platform && (
            <div className="mt-4 p-3 bg-primary-50 rounded-lg">
              <p className="text-sm text-primary-600">
                <span className="font-medium">Top Platform:</span>{' '}
                {PLATFORM_CONFIG[metrics.top_platform as SMCCPlatformId]?.name || metrics.top_platform}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
